// The app-wide setup, first: lodash and yup extensions the pages use, and the pages'
// stylesheets, which load with the app although the pages load on demand (see the file).
import './lodashMixins';
import './yupMixins';
import './pageStyles';
import React, { useEffect } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { buildRouter } from './router/routes';
import { getConfig } from './shared/api/configApi';
import installHttpInterceptor from './shared/api/httpInterceptor';

/**
 * The CMS, with no Angular in it.
 *
 * This replaces `web/app/app.js`, the `angular.module('cmsAdmin', [...])` bootstrap that ran
 * the admin from 2015 until SURF-4809. What it did in 300 lines of module list, route table
 * and `$templateCache` seeding is now a router and one interceptor.
 */
/*
 * The router is built first, then handed to the interceptor, because the interceptor's job is
 * to navigate and Angular's did so inside the SPA rather than by reloading the page.
 *
 * Both are at module scope. The router must not be rebuilt per render, which would reset
 * history and remount every page; and the interceptor must be installed before the first
 * request, which is `/config/config.json` and is exactly the one that answers with a redirect
 * to `/login` when there is no session.
 */
const router = buildRouter();

installHttpInterceptor((to) => router.navigate({ to, replace: true }));

const App = () => {
  // The document title, which was `<title message-key="cms.appName">` in index.html. Set
  // here rather than in the frame because the auth screens are outside the frame and should
  // still be titled.
  useEffect(() => {
    getConfig()
      .then((config) => {
        const appName = config?.messages?.['cms.appName'];
        if (appName) document.title = appName;
      })
      .catch(() => {
        // No config means no session, and the interceptor is already sending us to /login.
      });
  }, []);

  return <RouterProvider router={router} />;
};

export default App;
